import React, { useState, useMemo, useEffect } from "react";
import { AssetHelper } from "../../../ReactComponents/assetHelper";
import { OnDisplay } from "../../CollectionItemPage/OnDisplay/OnDisplay";
const Fade = require("react-reveal/Fade");

export interface ICollectionListItem {
    title: string;
    medium: string;
    primaryMedia: string;
    mediaId: string;
    imageUrl?: string;
    displayDate: string;
    invNo: string;
    linkToObjectPage: string;
    partOfExhibitions?: { exhibitionTitle: string; exhibitionPageLink: string }[];
    isVirtualObject: boolean;
    numberOfChildren: number;
    childrenPrimaryMedia: string[];
    onDisplayLabel?: string;
    versionsLabel?: string;
    isKeyWorksCarousel?: boolean;
}

function buildImageUrl(mediaId: string, width: string) {
    return mediaId
        ? `https://iiif.micr.io/${mediaId}/full/,${width}/0/default.jpg`
        : AssetHelper.images.placeholder;
}

export const CollectionListItem = ({
    title,
    medium,
    primaryMedia,
    mediaId,
    imageUrl = "",
    displayDate,
    invNo,
    linkToObjectPage,
    partOfExhibitions,
    isVirtualObject,
    numberOfChildren,
    childrenPrimaryMedia,
    onDisplayLabel,
    versionsLabel,
    isKeyWorksCarousel,
}: ICollectionListItem) => {
    const width = isKeyWorksCarousel ? "600" : "400";

    const {
        centerCandidates,
        leftCandidates,
        rightCandidates,
    } = useMemo(() => {
        const primaryUrl = mediaId ? buildImageUrl(mediaId, width) : undefined;

        const childUrls = (childrenPrimaryMedia ?? [])
            .filter(Boolean)
            .map((id) => buildImageUrl(id, width))
            .filter((v, i, arr) => arr.indexOf(v) === i);

        const centerCandidates = [
            ...(primaryUrl ? [primaryUrl] : []),
            ...childUrls,
            AssetHelper.images.placeholder,
        ];

        const leftCandidates = [
            ...childUrls,
            ...(primaryUrl ? [primaryUrl] : []),
        ];
        const rightCandidates = childUrls.length > 1
            ? [
                ...childUrls.slice(1),
                childUrls[0],
                ...(primaryUrl ? [primaryUrl] : []),
            ]
            : [
                ...childUrls,
                ...(primaryUrl ? [primaryUrl] : []),
            ];

        return { centerCandidates, leftCandidates, rightCandidates };
    }, [mediaId, childrenPrimaryMedia, width]);

    const [centerIdx, setCenterIdx] = useState(0);
    const centerSrc = centerCandidates[Math.min(centerIdx, centerCandidates.length - 1)];
    const handleCenterError = () => {
        setCenterIdx((i) => (i < centerCandidates.length - 1 ? i + 1 : i));
    };

    const [leftIdx, setLeftIdx] = useState(0);
    const [rightIdx, setRightIdx] = useState(0);
    const [leftUseCenter, setLeftUseCenter] = useState(false);
    const [rightUseCenter, setRightUseCenter] = useState(false);
    const [leftTriedCenter, setLeftTriedCenter] = useState(false);
    const [rightTriedCenter, setRightTriedCenter] = useState(false);

    useEffect(() => {
        if (!isVirtualObject) return;

        const bumpedByCenter = (
            candidates: string[],
            idx: number,
            setIdx: React.Dispatch<React.SetStateAction<number>>,
            useCenter: boolean
        ) => {
            if (useCenter) return;
            if (candidates[idx] === centerSrc && idx < candidates.length - 1) {
                setIdx(idx + 1);
            }
        };

        bumpedByCenter(leftCandidates, leftIdx, setLeftIdx, leftUseCenter);
        bumpedByCenter(rightCandidates, rightIdx, setRightIdx, rightUseCenter);
    }, [centerSrc, isVirtualObject]);

    const handleSideError = (
        side: "left" | "right",
        candidates: string[],
        idx: number,
        setIdx: React.Dispatch<React.SetStateAction<number>>,
        setUseCenter: React.Dispatch<React.SetStateAction<boolean>>,
        triedCenter: boolean,
        setTriedCenter: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
        if (idx < candidates.length - 1) {
            setIdx(idx + 1);
            return;
        }
        if (!triedCenter) {
            setUseCenter(true);
            setTriedCenter(true);
            return;
        }
        setUseCenter(false);
        setIdx(candidates.length);
    };

    const leftResolvedSrc = leftUseCenter
        ? centerSrc
        : (leftCandidates[leftIdx] ?? AssetHelper.images.placeholder);

    const rightResolvedSrc = rightUseCenter
        ? centerSrc
        : (rightCandidates[rightIdx] ?? AssetHelper.images.placeholder);

    return (
        <Fade duration={800} delay={10} distance="60px" bottom force>
            <li className={isVirtualObject ? "CollectionListVirtualItem" : "CollectionListItem"}>
                <a
                    href={`${linkToObjectPage}${invNo}`}
                    className={isVirtualObject ? "CollectionListVirtualItem__link" : "CollectionListItem__link"}
                >
                    {isVirtualObject ? (
                        <>
                            <div className="CollectionListVirtualItem__images">
                                <img
                                    className="CollectionListVirtualItem__image CollectionListVirtualItem__image--left"
                                    src={leftResolvedSrc}
                                    alt={title}
                                    onError={() =>
                                        handleSideError(
                                            "left",
                                            leftCandidates,
                                            leftIdx,
                                            setLeftIdx,
                                            setLeftUseCenter,
                                            leftTriedCenter,
                                            setLeftTriedCenter
                                        )
                                    }
                                />
                                <img
                                    className="CollectionListVirtualItem__image CollectionListVirtualItem__image--center"
                                    src={centerSrc}
                                    alt={title}
                                    onError={handleCenterError}
                                />
                                <img
                                    className="CollectionListVirtualItem__image CollectionListVirtualItem__image--right"
                                    src={rightResolvedSrc}
                                    alt={title}
                                    onError={() =>
                                        handleSideError(
                                            "right",
                                            rightCandidates,
                                            rightIdx,
                                            setRightIdx,
                                            setRightUseCenter,
                                            rightTriedCenter,
                                            setRightTriedCenter
                                        )
                                    }
                                />
                            </div>

                            <div className="CollectionListVirtualItem__text-container">
                                <h2 className="CollectionListVirtualItem__heading">{title}</h2>
                                <p className="CollectionListVirtualItem__text">{displayDate}</p>
                            </div>

                            <div className="CollectionListVirtualItem__footer">
                                <p className="CollectionListVirtualItem__footer-text">
                                    {versionsLabel.replace("{medium}", medium).replace("{0}", numberOfChildren.toLocaleString("en-US").replace(/,/g, " "))}
                                </p>
                            </div>

                            <div className="CollectionListVirtualItem__overlay">
                                <span className="CollectionListVirtualItem__overlay-text">{numberOfChildren}</span>
                            </div>
                        </>
                    ) : (
                        <>
                            <img
                                className="CollectionListItem__image"
                                src={imageUrl ? imageUrl : buildImageUrl(mediaId, width)}
                                alt={title}
                                onError={(e) => {
                                    const target = e.currentTarget;
                                    target.src = AssetHelper.images.placeholder;
                                }}
                            />
                            <div className="CollectionListItem__text-container">
                                <h2 className="CollectionListItem__heading">{title}</h2>
                                <p className="CollectionListItem__text">{medium}</p>
                                <p className="CollectionListItem__text">{displayDate}</p>
                            </div>
                        </>
                    )}
                </a>

                {partOfExhibitions && partOfExhibitions.length !== 0 && (
                    <OnDisplay
                        text={onDisplayLabel}
                        linkText={partOfExhibitions[0].exhibitionTitle}
                        link={partOfExhibitions[0].exhibitionPageLink}
                    />
                )}
            </li>
        </Fade>
    );
};
